import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import classNames from 'classnames'

import { MENU_POST_ITEM } from '../../../../constants/menu'
import { LOCAL_STORAGE_NAME_TOKEN } from '../../../../constants/storage'
import {
  useInitActions,
  useInitData,
  useInlineNotifications,
  useUk,
} from '../../../../hooks'
import { getWebappLinkPrefix } from '../../../../utils/route'
import { setStorageItem } from '../../../../utils/storage'
import { parseServerValidation } from '../../../../utils/validation'
import { FormikForm } from '../../../Formik'
import { hasNotifications } from '../../Auth.utils'
import { AuthFooter } from '../../AuthFooter'
import { AuthFormWrapper } from '../../AuthFormWrapper'
import { AuthHeader } from '../../AuthHeader'
import { AuthHelpText } from '../../AuthHelpText'
import { AuthInvalidHash } from '../../AuthInvalidHash'
import { AuthPanel } from '../../AuthPanel'
import { AuthTitle } from '../../AuthTitle'

import { CREATE_PASSWORD_FORM_INITIAL_VALUES } from './CreatePasswordForm.constants'
import { getCreatePasswordFormValidationSchema } from './CreatePasswordForm.utils'
import { CreatePasswordFormFields } from './CreatePasswordFormFields'

import * as styles from './CreatePasswordForm.module.css'

function CreatePasswordForm({
  getValidationSchema = getCreatePasswordFormValidationSchema,
  initialValues = CREATE_PASSWORD_FORM_INITIAL_VALUES,
  invalidHashProps,
  isValidHash,
  text = 'Auth.CreatePasswordTextKey',
  title = 'Auth.CreatePasswordTitleKey',
  onSubmit,
}) {
  const { resetApp } = useInitActions()

  const { t } = useTranslation()

  const navigate = useNavigate()

  const { hash } = useParams()

  const uk = useUk()

  const { availableSections } = useInitData()

  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const validationSchema = getValidationSchema()

  function handleSubmit(data, { setErrors, setSubmitting }) {
    onSubmit({ data, hash })
      .then(response => {
        if (response.data.profile_id) {
          setStorageItem(LOCAL_STORAGE_NAME_TOKEN, response.data.key)

          resetApp()

          if (availableSections[MENU_POST_ITEM.uiSectionName]) {
            navigate('/')
          } else {
            window.location.replace(getWebappLinkPrefix(undefined, true))
          }
        }
      })
      .catch(error => {
        setErrors(parseServerValidation(error))
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const withNotifications = hasNotifications(notifications)

  const titleClassName = classNames(styles.title, {
    [styles.withNotifications]: withNotifications,
  })

  return isValidHash ? (
    <AuthPanel withNotifications={withNotifications}>
      <AuthHeader notifications={notifications} />

      <AuthFormWrapper>
        <AuthTitle className={titleClassName} title={t(title)} />
        <AuthHelpText
          className={styles.helpText}
          text={t(text, { ukName: uk.name })}
        />
        <FormikForm
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <CreatePasswordFormFields
            onRemoveNotification={handleRemoveNotification}
            onSetNotification={handleAddNotification}
          />
        </FormikForm>
      </AuthFormWrapper>

      <AuthFooter withNotifications={withNotifications} />
    </AuthPanel>
  ) : (
    <AuthInvalidHash {...invalidHashProps} />
  )
}

export default CreatePasswordForm
